import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Project } from '../types';
import { generateUniqueId } from '../utils/scenarioUtils';

const API_BASE_URL = process.env.REACT_APP_API_HOST;

interface ProjectsContextValue {
  projects: Project[];
  selectedProject: Project | null;
  setProjects: (projects: Project[] | ((prev: Project[]) => Project[])) => void;
  setSelectedProject: (project: Project | null) => void;
  fetchProjects: () => Promise<void>;
  handleProjectsUpdate: (updatedProjects: unknown[]) => void;
  handleFilesUpdate: (newFiles: string[]) => void;
}

const ProjectsContext = createContext<ProjectsContextValue | null>(null);

function normalizeProject(project: any): Project {
  const csvFiles = Array.isArray(project.csvFiles)
    ? project.csvFiles.map((file: any) =>
        typeof file === 'string'
          ? { id: generateUniqueId(), name: file, originalName: file, testIds: [] }
          : {
              id: file.id || generateUniqueId(),
              name: file.name || file.originalName || '',
              originalName: file.originalName || file.name || '',
              testIds: Array.isArray(file.testIds) ? file.testIds : [],
            }
      )
    : [];

  return {
    id: project.id,
    name: project.name,
    description: project.description || '',
    csvFiles,
    createdAt: project.createdAt || new Date().toISOString(),
    updatedAt: project.updatedAt || new Date().toISOString(),
    gitlab: project.gitlab,
  };
}

export const ProjectsProvider: React.FC<{ children: React.ReactNode; isAuthenticated: boolean }> = ({
  children,
  isAuthenticated,
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const fetchProjects = useCallback(async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/api/projects`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch projects');
    const data = await response.json();
    setProjects(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProjects().catch(() => setProjects([]));
    }
  }, [isAuthenticated, fetchProjects]);

  const handleProjectsUpdate = useCallback((updatedProjects: unknown[]) => {
    const converted = (updatedProjects as any[]).map(normalizeProject);
    setProjects(converted);
  }, []);

  const handleFilesUpdate = useCallback((newFiles: string[]) => {
    setProjects(prev =>
      prev.map(project =>
        selectedProject && project.id === selectedProject.id
          ? {
              ...project,
              csvFiles: newFiles.map(file => ({
                id: generateUniqueId(),
                name: file,
                originalName: file,
                testIds: [],
              })),
            }
          : project
      )
    );
  }, [selectedProject]);

  const value: ProjectsContextValue = {
    projects,
    selectedProject,
    setProjects,
    setSelectedProject,
    fetchProjects,
    handleProjectsUpdate,
    handleFilesUpdate,
  };

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
};

export function useProjects(): ProjectsContextValue {
  const ctx = useContext(ProjectsContext);
  if (!ctx) throw new Error('useProjects must be used within ProjectsProvider');
  return ctx;
}
